import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthFrame from "./AuthFrame";
import { TitleBlock, Actions, PrimaryBtn, InlineLink, Fine, Note } from "./pieces";
import { OptionCard } from "../../ForAthletes/v2/onboarding/onboardingPieces";
import { BmModal, Btn } from "../../ForBrands/ui/BrandUI";
import { useIsPhone } from "../../ForBrands/useIsPhone";
import { clearSignup, readSignup, writeSignup } from "./signupState";
import { isVerified, provedMobileOnly, routeAfterVerify } from "./signupFlow";
import { ACCOUNT_TYPES, accountTypeOf, articleFor } from "./accountTypes";
import { formatPhone } from "./phoneFormat";

// The heading over the list: a fresh visit, or an identity already
// confirmed by Google, by an email code, or by a text code (which proves
// the mobile, not the email).
const introFor = (saved) => {
  if (!isVerified(saved)) {
    return { eyebrow: "Create an account", sub: "Pick the account that fits you. It shapes what you see first; you can add other roles later." };
  }
  if (saved.viaGoogle) {
    return { eyebrow: "Google account", sub: `Signed in with Google as ${saved.email} and confirmed. No account uses it yet, so pick the account you're creating.` };
  }
  if (provedMobileOnly(saved)) {
    return { eyebrow: "Verified mobile", sub: `${saved.sentTo || formatPhone(saved.phone)} is confirmed. Pick the account you're creating.` };
  }
  return { eyebrow: "Verified email", sub: `${saved.email} is confirmed. Pick the account you're creating.` };
};

// 00 · Account type: the first screen of sign-up. The pick is kept in the
// signup state and the welcome screen, Google and the email path act on it,
// so nobody is asked twice; a pick already in the state (Change on the
// welcome screen, an old /register/other?role= link) opens selected. When
// Google was used before any pick (log in screen), the identity arrives
// confirmed by Google and waits here, and so does a typed email or mobile
// that confirmed its code before a type was picked: every type then goes on
// to the name step.
const AccountTypePage = () => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(readSignup);
  const [picked, setPicked] = useState(() => accountTypeOf(saved.accountType).key);
  const [confirming, setConfirming] = useState(false);
  const type = accountTypeOf(picked);
  const pendingVerified = isVerified(saved);
  const intro = introFor(saved);
  // This page renders AuthFrame itself, so it sits outside the phone theme
  // and reads the viewport directly. On phones a long confirmed email
  // breaks inside the column instead of running past the screen edge.
  const onPhone = useIsPhone();

  const go = () => {
    setConfirming(false);
    if (pendingVerified) {
      navigate(routeAfterVerify(writeSignup({ accountType: picked })));
      return;
    }
    clearSignup();
    writeSignup({ accountType: picked });
    navigate("/welcome");
  };

  const startOver = () => { clearSignup(); setSaved({}); };

  return (
    <AuthFrame action={{ label: "Log in", onClick: () => navigate("/login") }}>
      <TitleBlock eyebrow={intro.eyebrow} title="Who are you joining as?" sub={onPhone ? <span className="break-words">{intro.sub}</span> : intro.sub} />
      {pendingVerified && (
        <p className="bm-body-s text-bm-muted">
          Not you? <button type="button" onClick={startOver} className="bm-label-xs text-bm-orange hover:underline">Start over</button>
        </p>
      )}
      <div className="flex flex-col gap-[10px]">
        {ACCOUNT_TYPES.map((t) => (
          <OptionCard key={t.key} selected={picked === t.key} onClick={() => setPicked(t.key)} title={t.title} sub={t.sub} />
        ))}
      </div>
      <Actions>
        <PrimaryBtn onClick={() => setConfirming(true)}>Continue</PrimaryBtn>
        <InlineLink text="Already have an account?" label="Log in" onClick={() => navigate("/login")} />
        <Fine>By continuing you agree to the Terms and Privacy Policy.</Fine>
      </Actions>

      {/* Inside AuthFrame's phone theme this opens as the bottom sheet
          (Figma 2466:18757) with the two actions stacked at 52px. The footer
          is written primary first, so the sheet keeps that order. */}
      {confirming && (
        <BmModal
          title="Confirm account type"
          onClose={() => setConfirming(false)}
          width={480}
          phoneFooterOrder="natural"
          footer={
            <>
              <Btn onClick={go}>Yes, continue as {type.short}</Btn>
              <Btn variant="secondary" onClick={() => setConfirming(false)}>Go back</Btn>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="bm-body-l text-bm-ink max-sm:break-words">
              You're creating {articleFor(type.title)} <span className="font-semibold">{type.title}</span> account{pendingVerified ? ` as ${[saved.firstName, saved.lastName].filter(Boolean).join(" ") || saved.email}` : ""}.
            </p>
            <Note>{pendingVerified ? type.nextConfirmed : type.next}</Note>
            <p className="bm-body-s text-bm-muted">This choice sets your onboarding and the tools you can use. It cannot be changed later without contacting support.</p>
          </div>
        </BmModal>
      )}
    </AuthFrame>
  );
};

export default AccountTypePage;
